import { isNil } from 'lodash';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { API } from '@shared/types/api';
import { JsonStorage } from '@shared/util/storage/JsonStorage';
import { ControlsState } from '../types';
import { useControlsState } from './ControlsContext';
import { useViewTemplateState } from './ViewTemplateContext';
import {
  COLLECTION_CRITERIA_QUERY_PARAM,
  VIEW_TEMPLATE_ID_QUERY_PARAM,
  encodeCollectionCriteriaQueryParam,
  useSyncQueryParams,
} from './collectionViewQueryParamOverrides';
import { isCollectionCriteriaEqual } from './isCollectionCriteriaEqual';
import { mapCollectionCriteriaToState } from './mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from './mapStateToCollectionCriteria';

// Backup only - the URL (kept in sync below) is the primary carrier of unsaved edits, since it
// can be copied/bookmarked/shared and survives independently of this device's storage. This TTL
// just covers the gap between an edit and this device's next visit/save/revert.
const COLLECTION_CRITERIA_TTL = 1000 * 60 * 60 * 24; // 1 day

interface Props {
  setIsModified: Dispatch<SetStateAction<boolean>>;
  resetState: (nextState: ControlsState) => void;
  /**
   * A collectionCriteria override read from the `collectionCriteria` query param at mount. When
   * present, it takes precedence over any localStorage override (see
   * `useCollectionCriteriaQueryParamOverride`).
   */
  queryParamCriteria: API.CollectionCriteriaFragment | null;
}

export function PreserveCollectionCriteria({
  setIsModified,
  resetState,
  queryParamCriteria,
}: Props) {
  const { savedViewTemplate, preserveEdits } = useViewTemplateState();
  const storageKey = `viewTemplate-${savedViewTemplate.id}-collectionCriteria`;
  const { filters, sorts, groups, availableFilters, availableSorts, availableGroups } =
    useControlsState();
  const syncQueryParams = useSyncQueryParams();

  useEffect(() => {
    // Saved criteria can reference components that have since been archived or removed
    // from the workflow template. Those components can't be filtered on (the API rejects
    // them) so they never make it into the reducer state - meaning `updatedCriteria`
    // never includes them. Comparing against the raw saved criteria would therefore
    // always report the view as modified (and the revert button would appear to do
    // nothing). Normalize the saved criteria through the same mapping the state went
    // through so archived/removed components are ignored on both sides of the comparison.
    const savedCriteria = savedViewTemplate.collectionCriteria ?? null;
    const savedState = mapCollectionCriteriaToState(
      savedCriteria,
      availableFilters,
      availableSorts,
      availableGroups,
    );
    const comparableSavedCriteria = mapStateToCollectionCriteria(savedState);
    const updatedCriteria = mapStateToCollectionCriteria({ filters, sorts, groups });
    const isModified = !isCollectionCriteriaEqual(comparableSavedCriteria, updatedCriteria);
    setIsModified(isModified);

    // Only persist unsaved edits to local storage/the URL for usages that opt in (e.g. the
    // primary collection view page, and only while it's the plain route actually being viewed -
    // see `CollectionViewContainer`). Other usages should only ever reflect the filters passed in.
    if (!preserveEdits) {
      // Actively clear rather than just skipping - `preserveEdits` can flip false after these
      // params were already written (e.g. navigating to a subroute), and nothing else clears them.
      syncQueryParams({
        [VIEW_TEMPLATE_ID_QUERY_PARAM]: null,
        [COLLECTION_CRITERIA_QUERY_PARAM]: null,
      });
      return;
    }
    if (isModified) {
      // The view has unsaved filter/sort/group edits, so build a shareable link that captures
      // them: write `collectionCriteria` plus the `viewTemplateId` those edits belong to, together,
      // behind a single debounce (avoids a false positive while switching views). `viewTemplateId`
      // is only ever written alongside `collectionCriteria` - it exists purely to pin the edits to
      // the right view in a copied/bookmarked link. A saved view needs neither param; resolution
      // falls back to the path segment / cached slug, and writing `viewTemplateId` on its own would
      // become a live override (see `useViewTemplateIdQueryParamOverride`) that pins the wrong view.
      const timer = setTimeout(() => {
        JsonStorage.set(storageKey, updatedCriteria, COLLECTION_CRITERIA_TTL);
        syncQueryParams({
          [VIEW_TEMPLATE_ID_QUERY_PARAM]: savedViewTemplate.id,
          [COLLECTION_CRITERIA_QUERY_PARAM]: encodeCollectionCriteriaQueryParam(updatedCriteria),
        });
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Back in sync with the saved view - clear the backup and both link params so there's no
      // stale override left representing a deviation that no longer exists.
      JsonStorage.remove(storageKey);
      syncQueryParams({
        [VIEW_TEMPLATE_ID_QUERY_PARAM]: null,
        [COLLECTION_CRITERIA_QUERY_PARAM]: null,
      });
    }
  }, [
    savedViewTemplate.id,
    savedViewTemplate.collectionCriteria,
    filters,
    sorts,
    groups,
    availableFilters,
    availableSorts,
    availableGroups,
    setIsModified,
    storageKey,
    preserveEdits,
    syncQueryParams,
  ]);

  useEffect(() => {
    if (!preserveEdits || !isNil(queryParamCriteria)) {
      // A query param override (applied below) takes precedence over any localStorage override.
      return;
    }
    // load collection criteria from storage
    async function loadCollectionCriteria() {
      const savedCollectionCriteria =
        await JsonStorage.get<API.CollectionCriteriaFragment>(storageKey);
      if (!isNil(savedCollectionCriteria)) {
        const nextState = mapCollectionCriteriaToState(
          savedCollectionCriteria,
          availableFilters,
          availableSorts,
          availableGroups,
        );
        resetState(nextState);
      }
    }
    loadCollectionCriteria();
  }, [
    storageKey,
    availableFilters,
    availableSorts,
    availableGroups,
    resetState,
    preserveEdits,
    queryParamCriteria,
  ]);

  useEffect(() => {
    if (!preserveEdits || isNil(queryParamCriteria)) {
      return;
    }
    const nextState = mapCollectionCriteriaToState(
      queryParamCriteria,
      availableFilters,
      availableSorts,
      availableGroups,
    );
    resetState(nextState);
    // Persist immediately (bypassing the debounce in the effect above), rather than waiting for
    // the general isModified/sync cycle to catch up - otherwise a fast refresh right after
    // opening the link could land before the debounced sync fires and lose the override. Unlike
    // before, the query param itself is left in the URL rather than stripped - it's only ever
    // read once at mount (see `useCollectionCriteriaQueryParamOverride`), and leaving it in place
    // means it stays a durable, shareable/bookmarkable record of the criteria being viewed.
    const normalizedCriteria = mapStateToCollectionCriteria(nextState);
    JsonStorage.set(storageKey, normalizedCriteria, COLLECTION_CRITERIA_TTL);
    syncQueryParams({
      [COLLECTION_CRITERIA_QUERY_PARAM]: encodeCollectionCriteriaQueryParam(normalizedCriteria),
    });
  }, [
    queryParamCriteria,
    availableFilters,
    availableSorts,
    availableGroups,
    resetState,
    preserveEdits,
    storageKey,
    syncQueryParams,
  ]);

  return null;
}
